import { useState, useRef, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";

interface ColumnHeaderMenuProps {
  onRename: () => void;
  onDelete: () => void;
  isDisplayField?: boolean;
  onToggleDisplayField?: () => void;
  /** №11.3.2 — the column is a module-owned field: rename/delete are disabled
   *  (the module owns its shape) and a "Part of X" note is shown. */
  locked?: boolean;
  lockedLabel?: string;
}

const MENU_WIDTH = 160;
const GAP = 4;

/**
 * Sprint 14a — the per-column admin menu (Rename / Delete), triggered by a small
 * chevron at the column header's right edge. Like the Sprint 12b <ActionMenu>, the
 * popover is rendered through a portal to `document.body` with `position: fixed`
 * computed from the trigger rect, so it escapes the table grid's `overflowX: auto`
 * clipping. Clicking the chevron must not sort the column, so the trigger stops
 * propagation.
 */
export function ColumnHeaderMenu({ onRename, onDelete, isDisplayField, onToggleDisplayField, locked, lockedLabel }: ColumnHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 2 * 32 + 8;
    const spaceBelow = window.innerHeight - r.bottom;
    const flip = spaceBelow < menuH + GAP && r.top > menuH + GAP;
    const top = flip ? Math.max(GAP, r.top - menuH - GAP) : r.bottom + GAP;
    const right = Math.max(GAP, window.innerWidth - r.right);
    setCoords({ top, right });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const onScrollResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(reposition);
    };
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const trigger: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    flexShrink: 0,
  };

  const popover: CSSProperties = {
    position: "fixed",
    top: coords?.top ?? 0,
    right: coords?.right ?? 0,
    minWidth: MENU_WIDTH,
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 200,
    fontFamily: "var(--font-sans)",
    visibility: coords ? "visible" : "hidden",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  };

  const itemStyle = (variant?: "danger"): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    height: 32,
    padding: "0 12px",
    border: "none",
    background: "transparent",
    fontSize: 13,
    fontWeight: 400,
    textAlign: "left",
    borderRadius: "var(--border-radius-sm)",
    color: variant === "danger" ? "var(--c-red-600)" : "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  });

  return (
    <>
      <button
        ref={triggerRef}
        className="kuvio-col-menu-trigger"
        style={trigger}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Column actions"
      >
        <i className="ti ti-chevron-down" style={{ fontSize: 14 }} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div ref={menuRef} role="menu" style={popover} onClick={(e) => e.stopPropagation()}>
            {locked && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px 8px", fontSize: 11, color: "var(--color-text-tertiary)", borderBottom: "0.5px solid var(--color-border-secondary)" }}>
                <i className="ti ti-lock" style={{ fontSize: 13 }} aria-hidden="true" />
                {lockedLabel ?? "Module field — locked"}
              </div>
            )}
            <button
              role="menuitem"
              style={{ ...itemStyle(), opacity: locked ? 0.4 : 1, cursor: locked ? "not-allowed" : "pointer" }}
              disabled={locked}
              title={locked ? "Module-owned fields can't be renamed" : undefined}
              onClick={() => {
                if (locked) return;
                setOpen(false);
                onRename();
              }}
            >
              <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden="true" />
              Rename
            </button>
            {onToggleDisplayField && (
              <button
                role="menuitem"
                style={itemStyle()}
                onClick={() => {
                  setOpen(false);
                  onToggleDisplayField();
                }}
              >
                <i className={`ti ti-${isDisplayField ? "star-off" : "star"}`} style={{ fontSize: 14 }} aria-hidden="true" />
                {isDisplayField ? "Remove as display field" : "Set as display field"}
              </button>
            )}
            <button
              role="menuitem"
              style={{ ...itemStyle("danger"), opacity: locked ? 0.4 : 1, cursor: locked ? "not-allowed" : "pointer" }}
              disabled={locked}
              title={locked ? "Module-owned fields can't be removed" : undefined}
              onClick={() => {
                if (locked) return;
                setOpen(false);
                onDelete();
              }}
            >
              <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden="true" />
              Delete
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
